import "react-bootstrap-typeahead/css/Typeahead.css";
import "./index.css";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Form, Image, Stack } from "react-bootstrap";
import { FaClock, FaVideo } from "react-icons/fa";
import { useNavigate, useParams, useSearchParams } from "react-router";

import * as api from "../../api/api.jsx";
import {
    useDroneConfigDocument,
    useLivestreamDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { useDroneWidgetDataRtdbDocument } from "../../api/rtdbWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import Layout, { Title } from "../../components/Layout/Layout.jsx";
import { getFirebaseStorageDownloadUrl } from "../../corvusFirebase.js";
import { useDroneOnline } from "../../hooks/useDroneOnline.jsx";
import urls from "../../urls.jsx";

function useCameraStream(livestreams, cameraName) {
    const cam = livestreams?.[cameraName] || null;

    return {
        available: !!cam,
        lastPicture: cam?.lastPicture ?? null,
        lastPictureUrl: cam?.lastPictureUrl ?? null,
    };
}

function useStaleIndicator(lastPicture, timeoutMs = 20_000) {
    const [isStale, setIsStale] = useState(true);

    useEffect(() => {
        // new picture → mark fresh, then schedule stale
        if (!lastPicture) {
            setIsStale(true);
            return;
        }
        setIsStale(false);
        const t = setTimeout(() => setIsStale(true), timeoutMs);
        return () => clearTimeout(t);
    }, [lastPicture, timeoutMs]);
    return isStale;
}

function useStorageUrl(path, directUrl = null) {
    const [url, setUrl] = useState(null);
    const reqId = useRef(0);
    const lastUsedId = useRef(0);

    useEffect(() => {
        if (directUrl) {
            setUrl(directUrl);
            return;
        }

        if (!path) {
            setUrl(null);
            lastUsedId.current = 0;
            return;
        }

        const id = ++reqId.current;

        // const start = performance.now()

        getFirebaseStorageDownloadUrl(
            `gs://corvus-backend.appspot.com/livestreams/${path}`,
        )
            .then((u) => {
                // const duration = performance.now() - start

                // console.log(
                //     `getDownloadURL took ${duration.toFixed(0)} ms`
                // )

                if (id > lastUsedId.current) {
                    lastUsedId.current = id;
                    setUrl(u);
                }
            })
            .catch(console.error);
    }, [path, directUrl]);

    return url;
}

export const LivestreamComponent = React.memo(function LivestreamComponent({
    droneId,
    cameraName,
}) {
    const [livestreams] = useLivestreamDocument(droneId);
    const { available, lastPicture, lastPictureUrl } = useCameraStream(
        livestreams,
        cameraName,
    );
    const isStale = useStaleIndicator(lastPicture);
    const isFirstUpdate = useRef(true);
    const [displayPicture, setDisplayPicture] = useState(null);
    // const lastFrameTime = useRef(null);

    // useEffect(() => {
    //     if (!lastPicture) {
    //         return;
    //     }

    //     const now = performance.now();

    //     if (lastFrameTime.current !== null) {
    //         const deltaMs = now - lastFrameTime.current;
    //         const fps = 1000 / deltaMs;

    //         console.log(
    //             `Livestream update: ${deltaMs.toFixed(0)} ms (${fps.toFixed(2)} FPS)`
    //         );
    //     }

    //     lastFrameTime.current = now;
    // }, [lastPicture]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Changing cameras deliberately resets the frame transition state.
    useEffect(() => {
        // Reset isFirstUpdate and displayPicture on camera change
        isFirstUpdate.current = true;
        setDisplayPicture(null);
    }, [cameraName]);

    useEffect(() => {
        if (lastPicture == null) {
            return;
        }
        if (isFirstUpdate.current) {
            // ignore the first update and reset the isFirstUpdate flag
            isFirstUpdate.current = false;
        } else {
            // Use this update, to update the displayed picture
            setDisplayPicture(lastPicture);
        }
    }, [lastPicture]);

    const imagePath =
        displayPicture && !isStale
            ? `${droneId}/${cameraName}/${displayPicture}`
            : "broadcast-interrupted.png";
    const imageUrl = useStorageUrl(
        imagePath,
        displayPicture && !isStale ? lastPictureUrl : null,
    );

    if (!cameraName) return <span>No camera selected</span>;
    if (!available) return <span>Camera {cameraName} not available</span>;

    return (
        <Image
            src={imageUrl}
            alt={`${cameraName}`}
            style={{
                maxHeight: "95vh", // don't exceed window height
                width: "100%", // scale horizontally
                objectFit: "contain", // keep aspect ratio, no crop
            }}
            rounded
            fluid
        />
    );
});

export default function LivestreamPage() {
    const { organizationId, locationId, droneId } = useParams();
    const [droneConfig] = useDroneConfigDocument(droneId);
    const [livestreams] = useLivestreamDocument(droneId);
    const cameraOptionsRef = useRef([]);
    const cameraOptions = useMemo(() => {
        if (!livestreams) return [];

        const lowerKeys = Object.keys(livestreams).map((k) => k.toLowerCase());
        const hasVio = lowerKeys.includes("vio");

        return Object.keys(livestreams)
            .filter((k) => k.toLowerCase() !== "id")
            .filter((k) => !(k.toLowerCase() === "qvio" && hasVio))
            .sort((a, b) =>
                a.localeCompare(b, undefined, { sensitivity: "base" }),
            );
    }, [livestreams]);

    useEffect(() => {
        cameraOptionsRef.current = cameraOptions; // cameraOptions is array of camera names (strings)
    }, [cameraOptions]);

    const [searchParams] = useSearchParams();
    const [currentCamera, setCurrentCamera] = React.useState(
        searchParams.get("cam") ?? "",
    );
    const [hasSentWakeUp, setHasSentWakeUp] = useState(false);
    const [droneIsSleeping] = useDroneWidgetDataRtdbDocument(
        droneId,
        "isSleeping",
    );
    const [isStreamEnabled, setIsStreamEnabled] = useState(undefined);
    const isDroneOnline = useDroneOnline(droneId);
    const navigate = useNavigate();

    // Set the current camera's state from Firestore on camera switch
    useEffect(() => {
        if (!currentCamera || !droneConfig) {
            setIsStreamEnabled(undefined);
            return;
        }

        const settingKey = Object.keys(api.STREAMER_SETTINGS_MAP).find(
            (key) => key.toLowerCase() === currentCamera.toLowerCase(),
        );

        if (settingKey) {
            const fieldName = api.STREAMER_SETTINGS_MAP[settingKey];
            const isEnabled = droneConfig?.streamer?.[fieldName] ?? false;
            setIsStreamEnabled(isEnabled);
        } else {
            setIsStreamEnabled(undefined);
        }
    }, [currentCamera, droneConfig]);

    const handleCameraChange = async (newCamera) => {
        const cameraBeingLeft = currentCamera;

        if (isStreamEnabled && cameraBeingLeft) {
            try {
                await api.setLivestreamEnabled(droneId, cameraBeingLeft, false);
            } catch (error) {
                console.error(
                    `Failed to disable stream for camera ${cameraBeingLeft}:`,
                    error,
                );
            }
        }
        setCurrentCamera(newCamera);
        navigate(
            urls.locationDroneLivestream(
                organizationId,
                locationId,
                droneId,
                newCamera,
            ),
            { replace: true },
        );
    };

    // Toggle livestream state
    const handleToggleStream = async (e) => {
        if (!currentCamera) return;

        const enabled = e.target.checked;
        setIsStreamEnabled(enabled);

        try {
            await api.setLivestreamEnabled(droneId, currentCamera, enabled);
            console.log(
                `${enabled ? "Enabled" : "Disabled"} stream successfully`,
            );
        } catch (error) {
            console.error("Failed to update stream state:", error);
            setIsStreamEnabled(!enabled); // Revert state if the update fails
        }
    };

    const numberOfStreamsEnabled = useMemo(() => {
        if (!droneConfig?.streamer) return 0;

        return cameraOptions.reduce((count, cameraName) => {
            const key = cameraName.toLowerCase();
            const settingName = api.STREAMER_SETTINGS_MAP[key];

            if (!settingName) return count;

            return droneConfig.streamer[settingName] === true
                ? count + 1
                : count;
        }, 0);
    }, [droneConfig, cameraOptions]);

    // unmount cleanup only
    useEffect(() => {
        return () => {
            const cams = cameraOptionsRef.current;

            if (droneId && Array.isArray(cams) && cams.length > 0) {
                api.setLivestreamEnabled(droneId, cams, false).catch(
                    console.error,
                );
            }
        };
    }, [droneId]);

    useEffect(() => {
        const handler = (e) => {
            if (numberOfStreamsEnabled === 0) {
                return;
            }

            e.preventDefault();
            e.returnValue = ""; // required by browsers
        };

        window.addEventListener("beforeunload", handler);

        return () => {
            window.removeEventListener("beforeunload", handler);
        };
    }, [numberOfStreamsEnabled]);

    return (
        <Layout>
            <title>{`Livestream ${droneId} | MyCorvus`}</title>
            <Breadcrumbs path={breadcrumbPaths.droneLivestream} />
            <Card className="mb-4">
                <Title
                    backButtonUrl={urls.drone(droneId)}
                    title={
                        <div className="input-group">
                            Camera:&nbsp;
                            <Form.Select
                                onChange={(e) =>
                                    handleCameraChange(e.target.value)
                                }
                                value={currentCamera}
                            >
                                {currentCamera === "" && (
                                    <option key="disabledOption" value={""}>
                                        {"Select a camera"}
                                    </option>
                                )}
                                {currentCamera !== "" && (
                                    <option
                                        key="disabledOption"
                                        value={""}
                                        disabled
                                    >
                                        {"Select a camera"}
                                    </option>
                                )}
                                {cameraOptions.map((option) => {
                                    return (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    );
                                })}
                            </Form.Select>
                        </div>
                    }
                />

                <Card.Body className="p-4 pt-0">
                    <div className="mb-2">
                        {!isDroneOnline && (
                            <Alert className="text-center" variant="secondary">
                                <div className="text-muted">
                                    Drone is offline
                                </div>
                            </Alert>
                        )}
                        <Stack
                            direction="vertical"
                            gap={2}
                            className="flex-md-row align-items-md-center justify-content-between mb-2"
                        >
                            {isStreamEnabled !== undefined && (
                                <Form.Check
                                    type="switch"
                                    id={`enable_${currentCamera}_stream`}
                                    label={`Enable ${currentCamera} stream`}
                                    checked={isStreamEnabled}
                                    onChange={handleToggleStream}
                                    className="mb-0"
                                />
                            )}

                            {numberOfStreamsEnabled > 0 && (
                                <Alert className="p-2 py-1 d-inline-flex align-items-center mb-0">
                                    <FaVideo className="me-2" />
                                    {numberOfStreamsEnabled === 1
                                        ? "1 livestream is enabled"
                                        : `${numberOfStreamsEnabled} livestreams are enabled`}
                                </Alert>
                            )}
                        </Stack>

                        {isDroneOnline && droneIsSleeping && (
                            <Button
                                className="px-2 py-1"
                                disabled={hasSentWakeUp}
                                onClick={() => {
                                    setHasSentWakeUp(true);
                                    api.sendWakeUp(droneId);
                                    setTimeout(() => {
                                        setHasSentWakeUp(false);
                                    }, 5000);
                                }}
                            >
                                <small>
                                    {hasSentWakeUp ? (
                                        <>
                                            <FaClock
                                                className="me-2"
                                                style={{ marginTop: "-2px" }}
                                            />
                                            Waking up {droneId}
                                        </>
                                    ) : (
                                        <>
                                            <FaClock
                                                className="me-2"
                                                style={{ marginTop: "-2px" }}
                                            />
                                            Wake up {droneId}
                                        </>
                                    )}
                                </small>
                            </Button>
                        )}
                    </div>
                    <LivestreamComponent
                        droneId={droneId}
                        cameraName={currentCamera}
                    />
                </Card.Body>
            </Card>
        </Layout>
    );
}
